// AWARENESS-MVP in the in-memory API (production: services/api/src/domain/org/seat-boards.ts):
// the boards a DE seat is told to use. `GET`/`PUT seats/{id}/boards`, owners and admins; the PUT
// replaces the whole set (each a live board here, named once, at most one Ideas board). A board
// archived since is left out when read.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import type { FakeCompany } from "./fake-data";
import type { Seat } from "./types";

type View = OperationOutput<"seats.boards">;
type Use = View["boards"][number];
type Stored = Pick<Use, "board_id" | "purpose" | "note">;

const PURPOSES = ["ideas", "pieces", "dates", "other"] as const;

export type SeatBoardsDeps = {
  json: (body: unknown, status?: number) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  forbidden: () => Response;
  seatsOf: (c: FakeCompany) => Seat[];
  /** A live (or archived) board of the company, or null. */
  boardOf: (
    companyId: string,
    boardId: string,
  ) => { name: string; archived_at: string | null } | null;
};

export function createSeatBoardsFake(d: SeatBoardsDeps) {
  const sets = new Map<string, Stored[]>();
  const key = (p: Principal, seatId: string) => `${p.company.id}:${seatId}`;
  const view = (p: Principal, seatId: string): View => ({
    seat_id: seatId,
    boards: (sets.get(key(p, seatId)) ?? []).flatMap((s) => {
      const b = d.boardOf(p.company.id, s.board_id);
      return b && !b.archived_at ? [{ ...s, board_name: b.name }] : [];
    }),
    editable: p.manager,
  });
  const handlers: Partial<Record<RouteId, Handler>> = {
    "seats.boards": ({ principal: p, params }) => {
      if (!p) return d.notFound();
      if (!p.manager) return d.forbidden();
      if (!d.seatsOf(p.company).some((s) => s.id === params.id)) return d.notFound();
      return d.json(view(p, params.id as string));
    },
    "seats.boardsUpdate": ({ principal: p, params, body }) => {
      if (!p) return d.notFound();
      if (!p.manager) return d.forbidden();
      const id = params.id as string;
      if (!d.seatsOf(p.company).some((s) => s.id === id)) return d.notFound();
      const b = (body ?? {}) as {
        boards?: { board_id?: unknown; purpose?: unknown; note?: unknown }[];
      };
      if (!Array.isArray(b.boards)) return d.validation("boards: required");
      const next: Stored[] = [];
      for (const [i, u] of b.boards.entries()) {
        const boardId = typeof u.board_id === "string" ? u.board_id : "";
        const live = d.boardOf(p.company.id, boardId);
        if (!live || live.archived_at)
          return d.validation(`boards.${i}.board_id: not a live board here`);
        if (next.some((x) => x.board_id === boardId))
          return d.validation(`boards.${i}.board_id: named twice`);
        if (!PURPOSES.includes(u.purpose as (typeof PURPOSES)[number]))
          return d.validation(`boards.${i}.purpose: ideas, pieces, dates or other`);
        next.push({
          board_id: boardId,
          purpose: u.purpose as Stored["purpose"],
          note: typeof u.note === "string" && u.note.trim() ? u.note.trim() : null,
        });
      }
      if (next.filter((x) => x.purpose === "ideas").length > 1)
        return d.validation("boards: at most one Ideas board per seat");
      sets.set(key(p, id), next);
      return d.json({ ...view(p, id), editable: true });
    },
  };
  return { handlers };
}
